import { defineStore } from 'pinia';
import type { PiniaPluginContext } from 'pinia';
import type {} from 'pinia-plugin-persistedstate';
import { readCookie } from '@/modules/cookies';
import { useTestModeStore } from '@/stores/testMode';
import { logger } from '@/logging/logger';
import { LogCategory } from '@/logging/types';
import { carePlanScope, hasWorkerSelection, isDetectedRisk } from '@/utils/carePlanScope';
import { RISK_SEVERITIES } from '@/utils/riskSeverities';

const triageOrder = ['none_selected', 'none', 'inconclusive', 'standard', 'immediate', 'urgent'];

// Re-exported: the danger-sign scoring reads the severities from here.
export { RISK_SEVERITIES };

// Superseded assessments stay in the bundle for collect; no screen shows them or their items.
const isHiddenIn = (bundle: any[]): ((item: any) => boolean) => {
  const hidden = new Set(bundle
    .filter(item => item.type === 'assessment' && item.display_hidden)
    .map(item => item.name));
  return item => Boolean(item.display_hidden) || hidden.has(item.assessment);
};

export interface MrxState {
  bundle: any[];
  engagementKey: string | null;
}

// bundle is load bearing: savedEncounters clones this object and resumes it back
export const defaultState = (): MrxState => ({
  bundle: [],
  engagementKey: null,
});

// destr returns the raw string for a bad payload, and $patch spreads its indices over the state.
const STATE_KEYS = new Set(Object.keys(defaultState()));

// A spread string leaves keys "0" to "N-1". Anything named is a field another build wrote.
const isSpreadIndex = (key: string): boolean => /^\d+$/.test(key);

function reportInvalidBundle(bundle: unknown): void {
  if (Array.isArray(bundle)) return;

  // Shape only: the bundle is the clinical assessment.
  logger.error(
    LogCategory.STORAGE,
    'storage.bundle.invalid',
    { type: typeof bundle, isArray: false },
    'Clinical bundle is not an array',
  ).then();
}

// Must not throw: hydrateStore swallows a hook error without reporting it.
export function afterHydrate(context: PiniaPluginContext): void {
  const state = context.store.$state as Record<string, unknown>;
  const unexpected = Object.keys(state).filter((key) => !STATE_KEYS.has(key));
  const spread = unexpected.filter(isSpreadIndex);
  const unknown = unexpected.filter((key) => !isSpreadIndex(key));

  if (unknown.length > 0) {
    // Reported and kept: a newer build wrote it, and deleting it loses that data on a downgrade.
    logger.warn(
      LogCategory.STORAGE,
      'storage.state.unknown_field',
      { unknownKeys: unknown.length },
      'Persisted clinical state carries fields this build does not know',
    ).then();
  }

  if (spread.length > 0) {
    logger.error(
      LogCategory.STORAGE,
      'storage.state.unreadable',
      { unexpectedKeys: spread.length },
      'Persisted clinical state could not be read and has been discarded',
    ).then();

    // $persist, not $patch: hydrateStore runs before the plugin registers its $subscribe.
    // Safe only while this store has one persistence: $persist writes every one of them.
    spread.forEach((key) => { delete state[key]; });
    context.store.$persist();
    return;
  }

  if (!Array.isArray(state.bundle)) {
    reportInvalidBundle(state.bundle);

    // Reset, not just reported: 13 of the 20 bundle reads are unguarded, so leaving a
    // non-array here throws through most of the store on the next render.
    state.bundle = [];
    context.store.$persist();
  }
}

// Exported for the spec: the plugin is ESM only, so jest cannot drive the hook through it.
export const persistOptions = { afterHydrate };

export const useMrxStore = defineStore('mrx', {
  persist: persistOptions,
  state: defaultState,

  getters: {
    // named apart from the state field, which pinia would otherwise shadow
    bundleSnapshot(state): any[] {
      return JSON.parse(JSON.stringify(state.bundle));
    },

    ready(state): boolean {
      const testModeStore = useTestModeStore();
      return testModeStore.get('alwaysPerformAssessment')
        || !testModeStore.get('uploadOnAssess')
        || (testModeStore.get('uploadOnAssess') && state.bundle.length === 0);
    },

    getUniquePlansByCategory: (state) => (category: string): any[] => {
      const planCategoryItems = state.bundle.filter(item => (
        item.type === 'plan' && item.category === category && item.shown
      ));
      const uniquePlans: any[] = [];
      planCategoryItems.forEach(plan => {
        const found = uniquePlans.find(p => p.name === plan.name);
        if (!found) {
          const { assessment, severity, selected, shown, ...rest } = plan;
          uniquePlans.push({ ...rest });
        }
      });
      return uniquePlans;
    },

    getSelectedTreatments: (state) => (): any[] => {
      const planSelectedItems = state.bundle.filter(item => item.type === 'plan' && item.selected);
      const uniqueSelectedItems = [...new Set(planSelectedItems.map(item => item.name))];
      return uniqueSelectedItems
        .map(planName => state.bundle.find(item => item.type === 'treatment' && item.name === planName))
        .filter(Boolean);
    },

    assessments: (state) => (names?: string[]): any[] => {
      let results: any[] = [];
      try {
        const hidden = isHiddenIn(state.bundle);
        if (names) {
          results = state.bundle.filter(item => (item.type === 'assessment'
            && names.includes(item.name) && !hidden(item)));
        }
        else {
          results = state.bundle.filter(item => (item.type === 'assessment' && !hidden(item)));
        }
      }
      // not converted: render path. detection lives at hydration.
      catch (e) { console.log(e) }
      return JSON.parse(JSON.stringify(results));
    },

    treatments: (state) => (names?: string[]): any[] => {
      let results: any[] = [];
      try {
        const hidden = isHiddenIn(state.bundle);
        if (names) {
          results = state.bundle.filter(item => (item.type === 'treatment'
            && names.includes(item.name) && !hidden(item)));
        }
        else {
          results = state.bundle.filter(item => (item.type === 'treatment' && !hidden(item)));
        }
      }
      // not converted: render path. detection lives at hydration.
      catch (e) { console.log(e) }
      return JSON.parse(JSON.stringify(results));
    },

    plans: (state) => (names?: string[]): any[] => {
      let results: any[] = [];
      try {
        const hidden = isHiddenIn(state.bundle);
        if (names) {
          results = state.bundle.filter(item => (item.type === 'plan'
            && names.includes(item.name) && !hidden(item)));
        }
        else {
          results = state.bundle.filter(item => (item.type === 'plan' && !hidden(item)));
        }
      }
      // not converted: render path. detection lives at hydration.
      catch (e) { console.log(e) }
      return JSON.parse(JSON.stringify(results));
    },

    triage(state): string {
      const triage = state.bundle.find(item => (item.type === 'triage'));
      return triage ? triage.name : 'none';
    },

    triageSelected: (state) => (): string => {
      const selectedAssessments = state.bundle.filter(item => {
        return item.type === 'assessment' && item.selected;
      });
      let triage = 'none_selected';
      selectedAssessments.forEach(assessment => {
        const triageRec = assessment.triage_recommendation;
        const currentTriageOrder = triageOrder.findIndex(x => x === triage);
        const newTriageOrder = triageOrder.findIndex(x => x === triageRec);
        if (newTriageOrder > currentTriageOrder) {
          triage = triageRec;
        }
      });
      return triage;
    },

    status(state): any[] {
      let results: any[] = [];
      try {
        results = state.bundle.filter(item => (item.type === 'status'));
      }
      // not converted: render path. detection lives at hydration.
      catch (e) { console.log(e) }
      return JSON.parse(JSON.stringify(results));
    },

    valid(state): boolean | undefined {
      const status = state.bundle.find(b => (b.type === 'status' && b.name === 'validity'));
      return status && status.value === 'valid';
    },

    notification(state): string | null {
      const notification = state.bundle.find(b => (b.type === 'notification' && b.name === 'mrx'));
      return notification ? notification.value : null;
    },

    hasRiskOfCondition: (state) => (name: string): boolean => {
      const condition = state.bundle.find(item => (item.type === 'assessment' && item.name === name));
      if (!condition) {
        return false;
      }
      return RISK_SEVERITIES.includes(condition.severity);
    },

    hasTreatmentsToShow(state): boolean {
      const hidden = isHiddenIn(state.bundle);
      const treatments = state.bundle.filter(item => (item.type === 'treatment' && !hidden(item)));
      const selectedAssessments = state.bundle
        .filter(item => (item.type === 'assessment' && item.selected && !hidden(item)))
        .map(item => item.name);

      if (treatments.length > 0 && selectedAssessments.length === 0) return true;

      for (const t of treatments) {
        if (selectedAssessments.includes(t.assessment)) return true;
      }
      return false;
    },

    hasCarePlansToShow: (state) => (): boolean => {
      const hidden = isHiddenIn(state.bundle);
      const plans = state.bundle.filter(item => (item.type === 'plan' && !hidden(item)));
      return plans.some(p => p.selected || (p.shown && p.category !== 'medication'));
    },
  },

  actions: {
    reset(): void {
      const engagementKey = this.engagementKey;
      this.$reset();
      this.$patch({ engagementKey });
    },

    resume(payload: Partial<MrxState>): void {
      this.$patch(payload);
    },

    setBundle(payload: any[]): void {
      reportInvalidBundle(payload);
      this.bundle = payload;
    },

    select(payload: { type: string, data: string[] }): void {
      const hidden = isHiddenIn(this.bundle);
      this.bundle = this.bundle.map(item => (
        item.type === payload.type
          ? { ...item, selected: payload.data.includes(item.name) && !hidden(item) }
          : item
      ));

      this.$patch({});
    },

    updateCareplanShownList(payload: string[]): void {
      const nothingPicked = !hasWorkerSelection(payload);
      const scope = nothingPicked
        ? this.assessments().filter(isDetectedRisk).map(a => a.name)
        : carePlanScope(this.assessments(), payload);
      const noneSelected = scope.length === 0;
      const hidden = isHiddenIn(this.bundle);
      this.bundle = this.bundle.map(item => (
        item.type === 'plan'
          ? { ...item, shown: !hidden(item) && (scope.includes(item.assessment) || noneSelected) }
          : item
      ));

      this.$patch({});
    },

    syncEngagement(): void {
      const key = readCookie('session');
      if (this.engagementKey === key) return;

      this.$reset();
      this.$patch({ engagementKey: key });
    },
  },
})
